import {
  CalendarDays,
  CircleAlert,
  Database,
  Layers,
  Lightbulb,
  Lock,
  MessageSquare,
  Repeat,
  ShieldAlert,
  Tag,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import type { InitiativeItem } from "../types/navigator";
import { barColor } from "../reviews";
import { formatScore } from "../utils/score";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { DetailRow, RichText, Row, formatDetailDate } from "./DetailParts";

function ScoreCell({ label, value }: { label: string; value: string }) {
  const n = Number(value);
  const has = Boolean(value) && !Number.isNaN(n);
  const pct = has ? Math.max(0, Math.min(100, (n / 5) * 100)) : 0;
  return (
    <div className="ini-glance-cell">
      <span className="ini-glance-label">{label}</span>
      {has ? (
        <span className="ini-score">
          <b>{formatScore(value)}</b>
          <small>/5</small>
        </span>
      ) : (
        <span className="ini-glance-value ini-muted">Not scored</span>
      )}
      <span className="ini-bar" aria-hidden>
        <span style={{ width: `${pct}%`, background: has ? barColor(n) : "transparent" }} />
      </span>
    </div>
  );
}

export function InitiativeDetails({ item }: { item: InitiativeItem }) {
  const isAdmin = useIsAdmin();
  const scored = Boolean(item.businessValueScore || item.technicalFeasibilityScore);
  const impactTypes = item.impactTypes?.length
    ? item.impactTypes
    : item.impactType
    ? [item.impactType]
    : [];
  const hasContext = Boolean(
    item.dataInputs || item.constraintsRisks || item.existingExamples || item.anythingElse
  );

  return (
    <div className="ini">
      {/* At a glance — one panel: key numbers in a row, impact types below */}
      <div className="ini-glance">
        <div className="ini-glance-row">
          {item.problemFrequency ? (
            <div className="ini-glance-cell">
              <span className="ini-glance-label">
                <Repeat size={12} aria-hidden /> How often
              </span>
              <span className="ini-glance-value">{item.problemFrequency}</span>
            </div>
          ) : null}
          {scored ? (
            <>
              <ScoreCell label="Business value" value={item.businessValueScore} />
              <ScoreCell label="Technical feasibility" value={item.technicalFeasibilityScore} />
            </>
          ) : (
            <div className="ini-glance-cell ini-glance-cell--compact">
              <span className="ini-pending">Not scored yet</span>
            </div>
          )}
        </div>
        {impactTypes.length ? (
          <div className="ini-glance-impact">
            <span className="ini-glance-label">
              <Tag size={12} aria-hidden /> Impact type
            </span>
            <span className="ini-chips">
              {impactTypes.map((t) => (
                <span key={t} className="ini-chip">
                  {t}
                </span>
              ))}
            </span>
          </div>
        ) : null}
      </div>

      {/* The story, in the order a reader thinks about it */}
      <dl className="ini-rows">
        <Row icon={CircleAlert} label="The problem" text={item.problem} />
        <Row icon={Users} label="Who's impacted" text={item.impactedToday} />
        <Row icon={Target} label="What success looks like" text={item.ifSolved} />
        <Row icon={TrendingUp} label="Anticipated impact" text={item.anticipatedImpact} />
        <Row icon={Lightbulb} label="Proposed solution" text={item.proposedSolution} />
      </dl>

      {hasContext ? (
        <section className="ini-section">
          <h4 className="ini-section-title">Context</h4>
          <dl className="ini-rows">
            <Row icon={Database} label="Data & inputs" text={item.dataInputs} />
            <Row icon={ShieldAlert} label="Constraints & risks" text={item.constraintsRisks} />
            <Row icon={Layers} label="Existing examples" text={item.existingExamples} />
            <Row icon={MessageSquare} label="Anything else" text={item.anythingElse} />
          </dl>
        </section>
      ) : null}

      <section className="ini-section">
        <h4 className="ini-section-title">
          <CalendarDays size={13} aria-hidden /> Details
        </h4>
        <dl className="ini-details">
          <DetailRow label="Team / function" value={item.team} />
          <DetailRow label="Region / division" value={item.region} />
          <DetailRow label="Status" value={item.status} />
          <DetailRow label="Priority" value={item.priority} />
          <DetailRow label="Rating" value={item.rating} />
          <DetailRow label="Development team" value={item.developmentTeam} />
          <DetailRow label="Submitted" value={formatDetailDate(item.created)} />
          <DetailRow label="Last updated" value={formatDetailDate(item.modified)} />
        </dl>
      </section>

      {isAdmin && item.notes ? (
        <section className="ini-notes">
          <div className="ini-notes-head">
            <Lock size={13} aria-hidden /> Admin notes <span>Internal only</span>
          </div>
          <RichText text={item.notes} />
        </section>
      ) : null}
    </div>
  );
}
